import React from 'react';
import { api } from '../services/api';

/** Matches MarketTicker's expected real-data shape. */
export interface MarketIndex {
  ticker: string;
  price: number | string;
  change_pct: number | string;
  chart_data?: { time: string; price: number; is_catalyst_event?: boolean }[];
}

/**
 * Loads scanner signals for the top marquee (same source as the scanner table).
 * Refreshes every 30 seconds to stay in sync with the scanner poll.
 */
export function useMarketIndices(): { indices: MarketIndex[]; isError: boolean } {
  const [indices, setIndices] = React.useState<MarketIndex[]>([]);
  const [isError, setIsError] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const data = await api.getSignals({ sort: 'change_pct_desc', limit: 100 });
      const next: MarketIndex[] = (data.signals || [])
        .filter((s) => s?.ticker && Number.isFinite(s.price))
        .map((s) => ({
          ticker: s.ticker,
          price: s.price,
          change_pct: s.change_pct,
          chart_data: (s as { chart_data?: MarketIndex['chart_data'] }).chart_data || [],
        }));

      setIndices(next);
      setIsError(next.length === 0);
    } catch {
      setIndices([]);
      setIsError(true);
    }
  }, []);

  React.useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  return { indices, isError };
}
